import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import { blogData } from '../../../mocks/blog';
import SeoHead from '../../../components/SeoHead';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = blogData.find((a) => a.slug === slug);
  const related = blogData.filter((a) => a.slug !== slug && a.category === article?.category).slice(0, 3);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.cjsolutionsltd.com';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const articleJsonLd = article
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          publisher: {
            '@type': 'Organization',
            name: 'CJ Solutions Ltd',
            url: siteUrl,
          },
          datePublished: article.date,
          url: `${siteUrl}/resources/${article.slug}`,
          articleSection: article.category,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${siteUrl}/resources/${article.slug}`,
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Resources', item: `${siteUrl}/resources` },
            { '@type': 'ListItem', position: 3, name: article.title, item: `${siteUrl}/resources/${article.slug}` },
          ],
        },
      ]
    : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center py-40 text-center px-4">
          <i className="ri-article-line text-6xl text-slate-300 mb-6 block"></i>
          <h1 className="text-3xl font-semibold text-slate-900 mb-4">Article Not Found</h1>
          <p className="text-slate-500 text-lg mb-8">The article you're looking for doesn't exist or has been moved.</p>
          <Link
            to="/resources"
            className="inline-block px-6 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Back to Resources
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title={`${article.title} | CJ Solutions Ltd`}
        description={article.excerpt}
        keywords={`${article.category}, CJ Solutions, compliance, Cyprus`}
        canonical={`/resources/${article.slug}`}
        jsonLd={articleJsonLd}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-0 overflow-hidden">
        <div className="relative w-full h-[480px] lg:h-[560px]">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-slate-900/20"></div>
          <div className="absolute inset-0 flex flex-col justify-end">
            <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-12">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block px-3 py-1 bg-amber-500 text-white text-xs font-semibold uppercase tracking-widest rounded-full">
                  {article.category}
                </span>
                <span className="text-white/70 text-sm flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="text-white/70 text-sm flex items-center gap-1">
                  <i className="ri-time-line"></i> {article.readTime}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-tight">
                {article.title}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="border-b border-slate-100 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/" className="hover:text-amber-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/resources" className="hover:text-amber-500 transition-colors">Resources</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-slate-900 font-medium line-clamp-1">{article.title}</span>
          </nav>
        </div>
      </div>

      {/* Article Body */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-12">

          {/* Content */}
          <article>
            {/* Author */}
            <div className="flex items-center gap-4 mb-10 pb-10 border-b border-slate-100">
              <div className="w-14 h-14 bg-slate-900 rounded-full flex items-center justify-center text-white text-xl font-semibold flex-shrink-0">
                {article.author.charAt(0)}
              </div>
              <div>
                <p className="text-slate-900 font-semibold text-base">{article.author}</p>
                <p className="text-slate-500 text-sm">{article.authorRole} · CJ Solutions</p>
              </div>
            </div>

            {/* Intro */}
            <p className="text-slate-700 text-lg leading-relaxed mb-10 font-light">
              {article.content.intro}
            </p>

            {/* Sections */}
            <div className="space-y-10">
              {article.content.sections.map((section, i) => (
                <div key={i}>
                  <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 bg-amber-500/10 text-amber-600 rounded-md flex items-center justify-center text-sm font-bold mt-0.5">
                      {i + 1}
                    </span>
                    {section.heading}
                  </h2>
                  <p className="text-slate-600 leading-relaxed text-base pl-10">
                    {section.body}
                  </p>
                </div>
              ))}
            </div>

            {/* Conclusion */}
            <div className="mt-12 p-8 bg-slate-50 rounded-2xl border border-slate-100">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-lightbulb-line text-amber-500 text-xl"></i>
                <span className="text-amber-600 font-semibold text-sm uppercase tracking-widest">Key Takeaway</span>
              </div>
              <p className="text-slate-700 leading-relaxed text-base">
                {article.content.conclusion}
              </p>
            </div>

            {/* Share */}
            <div className="mt-10 pt-10 border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-slate-500 text-sm font-medium">Share this article:</p>
              <div className="flex items-center gap-3">
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-100 transition-colors cursor-pointer"
                  aria-label="Share on LinkedIn"
                >
                  <i className="ri-linkedin-fill text-slate-700"></i>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(article.title)}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-100 transition-colors cursor-pointer"
                  aria-label="Share on Twitter"
                >
                  <i className="ri-twitter-x-fill text-slate-700"></i>
                </a>
                <button
                  onClick={() => navigator.clipboard.writeText(window.location.href)}
                  className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-100 transition-colors cursor-pointer"
                  aria-label="Copy link"
                >
                  <i className="ri-link text-slate-700"></i>
                </button>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="space-y-8">
            <div className="bg-slate-900 rounded-2xl p-7 text-white">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-amber-400 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold mb-3 leading-snug">Need expert advice on this topic?</h3>
              <p className="text-white/60 text-sm leading-relaxed mb-5">
                Our specialists are ready to help you navigate compliance, licensing, and regulatory requirements.
              </p>
              <Link
                to="/#contact"
                className="block w-full text-center py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                Book Free Consultation
              </Link>
            </div>

            {related.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-widest mb-5">
                  Related Articles
                </h3>
                <div className="space-y-5">
                  {related.map((rel) => (
                    <div
                      key={rel.slug}
                      onClick={() => navigate(`/resources/${rel.slug}`)}
                      className="group flex gap-4 cursor-pointer"
                    >
                      <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-900 text-sm font-medium leading-snug group-hover:text-amber-500 transition-colors line-clamp-2">
                          {rel.title}
                        </p>
                        <p className="text-slate-400 text-xs mt-1">{rel.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Link
              to="/resources"
              className="flex items-center gap-2 text-slate-600 hover:text-amber-500 transition-colors text-sm font-medium cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to all articles
            </Link>
          </aside>
        </div>
      </main>

      {related.length > 0 && (
        <section className="bg-slate-50 border-t border-slate-100 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-10">
              <h2 className="text-2xl font-semibold text-slate-900">More in {article.category}</h2>
              <Link
                to="/resources"
                className="text-amber-500 font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all whitespace-nowrap"
              >
                View All <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((rel) => (
                <article
                  key={rel.slug}
                  onClick={() => navigate(`/resources/${rel.slug}`)}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-slate-100"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={rel.image}
                      alt={rel.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="inline-block px-3 py-1 bg-white text-slate-900 text-xs font-semibold rounded-full">
                        {rel.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-sm text-slate-500 mb-3">
                      <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{rel.date}</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i>{rel.readTime}</span>
                    </div>
                    <h3 className="text-base font-semibold text-slate-900 mb-2 group-hover:text-amber-500 transition-colors line-clamp-2 leading-snug">
                      {rel.title}
                    </h3>
                    <p className="text-slate-500 text-sm line-clamp-2 leading-relaxed">{rel.excerpt}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-semibold text-slate-900 mb-4">Have a specific question?</h2>
          <p className="text-slate-600 text-lg mb-8">
            Our compliance and licensing experts are ready to help. Book a free consultation today.
          </p>
          <Link
            to="/#contact"
            className="inline-block px-8 py-4 bg-amber-500 text-white text-lg font-semibold rounded-lg hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Book a Free Consultation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
